import { normalizeEmail } from "./utils";

export const ANONYMOUS_VOTER_NAMES = Object.freeze([
  "Anonymous Paladin",
  "Anonymous Rogue",
  "Anonymous Wizard",
  "Anonymous Cleric",
  "Anonymous Ranger",
  "Anonymous Druid",
  "Anonymous Bard",
  "Anonymous Warlock",
  "Anonymous Monk",
  "Anonymous Sorcerer",
  "Anonymous Barbarian",
  "Anonymous Fighter",
  "Anonymous Artificer",
  "Anonymous Alchemist",
  "Anonymous Arcanist",
  "Anonymous Battlemage",
  "Anonymous Beastmaster",
  "Anonymous Bladesinger",
  "Anonymous Cartographer",
  "Anonymous Champion",
  "Anonymous Chronomancer",
  "Anonymous Corsair",
  "Anonymous Diviner",
  "Anonymous Dragoon",
  "Anonymous Duelist",
  "Anonymous Enchanter",
  "Anonymous Explorer",
  "Anonymous Farseer",
  "Anonymous Gladiator",
  "Anonymous Guardian",
  "Anonymous Harbinger",
  "Anonymous Herbalist",
  "Anonymous Illusionist",
  "Anonymous Inquisitor",
  "Anonymous Loremaster",
  "Anonymous Marauder",
  "Anonymous Necromancer",
  "Anonymous Outrider",
  "Anonymous Pathfinder",
  "Anonymous Runekeeper",
  "Anonymous Sentinel",
  "Anonymous Shadowblade",
  "Anonymous Spellblade",
  "Anonymous Stormcaller",
  "Anonymous Tactician",
  "Anonymous Thaumaturge",
  "Anonymous Trickster",
  "Anonymous Warden",
  "Anonymous Wildheart",
  "Anonymous Witch Knight",
]);

function hashString(value) {
  const normalized = String(value || "");
  let hash = 0;
  for (let index = 0; index < normalized.length; index += 1) {
    hash = ((hash << 5) - hash) + normalized.charCodeAt(index);
    hash |= 0; // Convert to 32-bit integer.
  }
  return Math.abs(hash);
}

export function resolveUserIdentityKey(user = {}, fallbackKey = null) {
  // Honor both .id and .uid (Firebase auth objects use .uid) for the id rung.
  const userId = String(user?.id || user?.uid || "").trim();
  if (userId) return `id:${userId}`;

  const email = normalizeEmail(user?.email);
  if (email) return `email:${email}`;

  const publicIdentifier = String(user?.publicIdentifier || "").trim().toLowerCase();
  if (publicIdentifier) return `public:${publicIdentifier}`;

  const displayName = String(user?.displayName || "").trim().toLowerCase();
  if (displayName) return `name:${displayName}`;

  const fallback = String(fallbackKey || "").trim().toLowerCase();
  if (fallback) return `fallback:${fallback}`;
  return null;
}

// Returns every non-empty identity key for a user across all rungs, including
// the weak `name:` rung. Useful for diagnostics / broad matching, but NOT for
// self-matching (two distinct people can share a displayName).
export function getUserIdentityKeys(user = {}) {
  const keys = getSelfMatchKeys(user);

  const displayName = String(user?.displayName || "").trim().toLowerCase();
  if (displayName) keys.add(`name:${displayName}`);

  return keys;
}

// STRONG identity keys only (id/uid, email, publicIdentifier) — never the
// weak `name:` rung. Used for self-exemption: matching on displayName would
// wrongly treat a *different* user who happens to share the viewer's name as
// "self" and render their REAL identity (a leak). Strong keys are unique.
export function getSelfMatchKeys(user = {}) {
  const keys = new Set();

  const id = String(user?.id || user?.uid || "").trim();
  if (id) keys.add(`id:${id}`);

  const email = normalizeEmail(user?.email);
  if (email) keys.add(`email:${email}`);

  const publicIdentifier = String(user?.publicIdentifier || "").trim().toLowerCase();
  if (publicIdentifier) keys.add(`public:${publicIdentifier}`);

  return keys;
}

function identityKeysIntersect(left, right) {
  if (!left || !right || left.size === 0 || right.size === 0) return false;
  const [smaller, larger] = left.size <= right.size ? [left, right] : [right, left];
  for (const value of smaller) {
    if (larger.has(value)) return true;
  }
  return false;
}

// Exported for the vote-visibility participant-view predicates (self-exemption
// check). Pure re-export — behavior is unchanged for existing internal callers.
export { identityKeysIntersect };

function buildAnonymousIdentity(displayName, scopeKey, identityKey) {
  const syntheticKey = hashString(`${scopeKey}:${identityKey}:${displayName}`).toString(36);
  return {
    id: `anon-${syntheticKey}`,
    email: `anon:${displayName.toLowerCase()}`,
    displayName,
    publicIdentifier: displayName,
    avatar: null,
    isAnonymous: true,
  };
}

export function buildAnonymousIdentityMap(users = [], { scopeKey = "global" } = {}) {
  const scope = String(scopeKey || "global").trim() || "global";
  const identityKeys = Array.from(
    new Set(
      (Array.isArray(users) ? users : [])
        .map((user, index) => resolveUserIdentityKey(user, `u-${index}`))
        .filter(Boolean)
    )
  ).sort((left, right) => left.localeCompare(right));

  const aliasCountByBase = new Map();
  const usedNameIndexes = new Set();
  const identityMap = new Map();

  identityKeys.forEach((identityKey) => {
    const preferredIndex = hashString(`${scope}:${identityKey}`) % ANONYMOUS_VOTER_NAMES.length;
    let chosenIndex = preferredIndex;
    let attempts = 0;

    while (
      attempts < ANONYMOUS_VOTER_NAMES.length &&
      usedNameIndexes.has(chosenIndex)
    ) {
      attempts += 1;
      chosenIndex = (chosenIndex + 1) % ANONYMOUS_VOTER_NAMES.length;
    }

    if (attempts < ANONYMOUS_VOTER_NAMES.length) {
      usedNameIndexes.add(chosenIndex);
    }

    const baseName = ANONYMOUS_VOTER_NAMES[chosenIndex];
    const nextBaseCount = (aliasCountByBase.get(baseName) || 0) + 1;
    aliasCountByBase.set(baseName, nextBaseCount);
    const displayName = nextBaseCount === 1 ? baseName : `${baseName} ${nextBaseCount}`;

    identityMap.set(
      identityKey,
      buildAnonymousIdentity(displayName, scope, identityKey)
    );
  });

  return identityMap;
}

// Generic scrubbed identity for the fail-closed path (a Map was provided but
// the user wasn't seeded into it). Never carries any real field.
const UNKNOWN_ANONYMOUS_IDENTITY = Object.freeze({
  id: "anon-unknown",
  email: "",
  displayName: "Anonymous voter",
  publicIdentifier: "Anonymous voter",
  avatar: null,
  isAnonymous: true,
});

export function anonymizeUser(user = {}, identityMap, fallbackKey = null) {
  // No map = explicit "not anonymizing" signal → pass the real user through.
  if (!(identityMap instanceof Map)) return user;

  const identityKey = resolveUserIdentityKey(user, fallbackKey);
  const anonymousIdentity = identityKey ? identityMap.get(identityKey) : null;

  // FAIL CLOSED: a Map was provided (we ARE anonymizing) but this user has no
  // alias (unkeyable, or its superset wasn't seeded). Never return the real
  // user — a map-miss must not leak a real name/email, and the stub carries
  // isAnonymous so it can't slip into VoterModal's named/friend-action branch.
  if (!anonymousIdentity) return { ...UNKNOWN_ANONYMOUS_IDENTITY };

  // WHITELIST scrub: emit ONLY known-safe fields. Never spread `...user`,
  // which would leak identity-bearing fields (qsUsername, discordUsername,
  // uid, publicIdentifierType, photoURL, etc.).
  return {
    id: anonymousIdentity.id,
    email: anonymousIdentity.email,
    displayName: anonymousIdentity.displayName,
    publicIdentifier: anonymousIdentity.publicIdentifier,
    avatar: null,
    isAnonymous: true,
  };
}

export function anonymizeUsers(
  users = [],
  identityMap,
  { keyPrefix = "user", currentUser = null } = {}
) {
  const source = Array.isArray(users) ? users : [];
  // Self-match on STRONG keys only (id/email/public) — never displayName, or a
  // different user sharing the viewer's name would be wrongly un-anonymized.
  const selfKeys = currentUser ? getSelfMatchKeys(currentUser) : null;

  return source.map((user, index) => {
    // Self-exempt: if this user's strong identity keys intersect the current
    // viewer's, return the REAL user (so self always sees self, R10).
    if (selfKeys && identityKeysIntersect(getSelfMatchKeys(user), selfKeys)) {
      return user;
    }
    return anonymizeUser(user, identityMap, `${keyPrefix}-${index}`);
  });
}
